/**
 * Redux Store Configuration
 * Configures the Redux store with all application slices
 */

import { configureStore } from '@reduxjs/toolkit';
import navigationReducer, { navigationListener } from '@/shared/providers/state/navigationSlice';
import userSettingsSlice from '@/shared/providers/userSettings';
import themeSlice from '@/shared/providers/state/themeSlice';
import authReducer from '@/shared/providers/state/authSlice';

/**
 * Configure the Redux store with all slices
 */
export const makeStore = () => {
  return configureStore({
    reducer: {
      navigation: navigationReducer,
      userSettings: userSettingsSlice.reducer,
      theme: themeSlice.reducer,
      auth: authReducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Ignore these action types
          ignoredActions: ['navigation/setRouteInfo'],
          // Ignore these field paths in all actions
          ignoredActionPaths: ['payload.icon', 'payload.breadcrumbs'],
          // Ignore these paths in the state
          ignoredPaths: ['navigation.routeInfo.breadcrumbs'],
        },
        // Saves the sidebar expand/collapse toggle to localStorage (navigation-store).
      }).prepend(navigationListener.middleware),
  });
};

// Create store instance
export const store = makeStore();

// Infer the type of makeStore
export type AppStore = ReturnType<typeof makeStore>;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];

/**
 * Theme hydrate thunk-like action creator.
 * ThemeProvider dispatches this then applies storage + OS preference.
 * Payload may include partial theme overrides; empty payload = read storage.
 */
export const hydrate = (state: Partial<RootState> | Record<string, unknown> = {}) => {
  return { type: 'app/hydrate' as const, payload: state ?? null };
};

// Selector for current theme (placeholder)
export const selectCurrentTheme = (state: RootState) => {
  // Return default theme if not found
  return 'mechagora';
};

// =============================================================================
// RE-EXPORT THEME ACTIONS (for backward compatibility)
// =============================================================================

export {
  setMode,
  toggle,
  setColorTheme,
  setSystemPreference,
  setInitialized,
  updateConfig,
  setTransitioning,
  resetTheme,
} from '@/shared/providers/state/themeSlice';

// =============================================================================
// RE-EXPORT TYPED HOOKS (for backward compatibility)
// =============================================================================

export { useAppDispatch, useAppSelector, useAppStore } from '@/shared/providers/hooks';
